import { useEffect, useMemo, useState } from 'react';
import {
  ANNOUNCEMENT_DEFAULTS,
  ANNOUNCEMENT_LIMITS,
  ANNOUNCEMENT_TONES,
  POPUP_AUDIENCES,
  POPUP_DEVICES,
  POPUP_KINDS,
  POPUP_LIMITS,
  POPUP_PAGES,
  POPUP_TRIGGERS,
  type AnnouncementSetting,
  type PopupDto,
  type PopupInput,
  type PopupKind,
  type PopupPage,
} from '@ecom/contracts';
import { AsyncSelect } from '@/components/async-select';
import { BilingualField, Choices, SelectField, TextField, ToggleField, type BilingualValue } from '@/components/fields';
import { MegaphoneIcon, PlusIcon } from '@/components/icons';
import { AnnotatedSection, ListCard, LoadMore, Page, SaveBar, Sheet, Table, TableScroll, Tabs, Td, Th, Tr } from '@/components/page';
import { Alert, Button, Card, ConfirmDialog, EmptyState, Field, Input, Pill, Skeleton, Switch, cx } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { fromLocalInput, toLocalInput } from '@/lib/date-input';
import { count, share } from '@/lib/format';
import { couponOptions } from '@/lib/options';
import { MediaField } from '@/pages/home-layout/fields';
import { useSettings } from '@/pages/settings/schema';
import { settingValue } from '@/stores/settings.store';
import { usePopupsStore } from '@/stores/popups.store';

type Tab = 'popups' | 'bar';
const BAR_KEY = 'marketing.announcement';

/**
 * Marketing → Popups & bar (ADR-098): the popups that ask at the right moment,
 * with what each did — views, clicks, signups — and the bar above every page.
 */
export function PopupsPage() {
  const t = useT();
  const [tab, setTab] = useState<Tab>('popups');

  return (
    <Page
      title={t('popupsPage')}
      subtitle={t('popupsPageHint')}
      tabs={
        <Tabs
          label={t('popupsPage')}
          value={tab}
          onChange={setTab}
          items={[
            { id: 'popups', label: t('popupsTab') },
            { id: 'bar', label: t('announcementTab') },
          ]}
        />
      }
    >
      {tab === 'popups' ? <PopupList /> : <AnnouncementForm />}
    </Page>
  );
}

// ─── Popups ──────────────────────────────────────────────────────────────────

/** Where a popup stands now: on and live, waiting for its start, past its end, or off. */
function stateOf(popup: PopupDto, now = Date.now()): 'live' | 'scheduled' | 'ended' | 'off' {
  if (!popup.isActive) return 'off';
  if (popup.endsAt && Date.parse(popup.endsAt) <= now) return 'ended';
  if (popup.startsAt && Date.parse(popup.startsAt) > now) return 'scheduled';
  return 'live';
}

const STATE_TONE = { live: 'success', scheduled: 'info', ended: 'neutral', off: 'neutral' } as const;

function PopupList() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { items, nextCursor, loading, error, load, loadMore, toggle, remove } = usePopupsStore();
  const [editing, setEditing] = useState<PopupDto | 'new' | null>(null);
  const [deleting, setDeleting] = useState<PopupDto | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      {error ? <Alert>{error}</Alert> : null}
      <ListCard>
        <div className="flex items-center justify-between gap-3 border-b border-line p-4">
          <p className="text-sm text-ink-muted">{t('popupsListHint')}</p>
          <Button onClick={() => setEditing('new')}>
            <PlusIcon className="size-4" />
            {t('popupNew')}
          </Button>
        </div>
        {loading && items.length === 0 ? (
          <Skeleton className="m-4 h-40" />
        ) : items.length === 0 ? (
          <EmptyState title={t('popupsEmpty')} body={t('popupsEmptyHint')} icon={<MegaphoneIcon />} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('popupName')}</Th>
                  <Th>{t('popupWhen')}</Th>
                  <Th align="end">{t('popupViews')}</Th>
                  <Th align="end">{t('popupResult')}</Th>
                  <Th align="end">{t('popupOn')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((popup) => {
                  const state = stateOf(popup);
                  const result = popup.kind === 'NEWSLETTER' ? popup.signups : popup.clicks;
                  return (
                    <Tr key={popup.id} onClick={() => setEditing(popup)} className="cursor-pointer">
                      <Td>
                        <span className="block font-medium text-ink">{popup.name}</span>
                        <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-ink-muted">
                          <Pill tone={STATE_TONE[state]}>{t(`popupState_${state}`)}</Pill>
                          {t(`popupKind_${popup.kind}`)}
                          {popup.discount ? (
                            <span dir="ltr" className="font-mono">
                              · {popup.discount.code}
                            </span>
                          ) : null}
                        </span>
                      </Td>
                      <Td className="text-sm text-ink-muted">
                        {t(`popupTrigger_${popup.trigger}`, { n: String(popup.triggerValue) })}
                        <span className="block text-xs">{popup.pages.map((page) => t(`popupPage_${page}`)).join('، ')}</span>
                      </Td>
                      <Td align="end">{count(popup.views, locale)}</Td>
                      <Td align="end">
                        {count(result, locale)}
                        <span className="block text-xs text-ink-muted">
                          {t(popup.kind === 'NEWSLETTER' ? 'popupSignups' : 'popupClicks')} · {share(result, popup.views, locale)}
                        </span>
                      </Td>
                      <Td align="end">
                        <span className="inline-flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
                          <Switch checked={popup.isActive} onChange={() => void toggle(popup)} label={t('popupOn')} />
                          <Button variant="ghost" size="sm" onClick={() => setDeleting(popup)}>
                            {t('delete')}
                          </Button>
                        </span>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        )}
        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>

      {editing ? <PopupSheet popup={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      <ConfirmDialog
        open={deleting !== null}
        title={t('popupDeleteTitle')}
        body={t('popupDeleteBody', { name: deleting?.name ?? '' })}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        tone="danger"
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) void remove(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

interface Draft {
  name: string;
  kind: PopupKind;
  isActive: boolean;
  title: BilingualValue;
  body: BilingualValue;
  cta: BilingualValue;
  image: { key: string; url: string } | null;
  ctaHref: string;
  discountId: string;
  trigger: PopupDto['trigger'];
  triggerValue: string;
  pages: PopupPage[];
  audience: PopupDto['audience'];
  devices: PopupDto['devices'];
  frequencyDays: string;
  priority: string;
  startsAt: string;
  endsAt: string;
}

function draftOf(popup: PopupDto | null): Draft {
  const pair = (field: 'title' | 'body' | 'cta'): BilingualValue => ({ ar: popup?.content.ar[field] ?? '', en: popup?.content.en[field] ?? '' });
  return {
    name: popup?.name ?? '',
    kind: popup?.kind ?? 'NEWSLETTER',
    isActive: popup?.isActive ?? false,
    title: pair('title'),
    body: pair('body'),
    cta: pair('cta'),
    image: popup?.imageKey && popup.imageUrl ? { key: popup.imageKey, url: popup.imageUrl } : null,
    ctaHref: popup?.ctaHref ?? '',
    discountId: popup?.discount?.id ?? '',
    trigger: popup?.trigger ?? 'DELAY',
    triggerValue: String(popup?.triggerValue ?? 8),
    pages: popup?.pages ?? ['all'],
    audience: popup?.audience ?? 'all',
    devices: popup?.devices ?? 'all',
    frequencyDays: String(popup?.frequencyDays ?? 7),
    priority: String(popup?.priority ?? 0),
    startsAt: toLocalInput(popup?.startsAt),
    endsAt: toLocalInput(popup?.endsAt),
  };
}

function whole(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
}

function PopupSheet({ popup, onClose }: { popup: PopupDto | null; onClose: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { save, saving, saveError } = usePopupsStore();
  const [draft, setDraft] = useState<Draft>(() => draftOf(popup));
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));

  const togglePage = (page: PopupPage) => {
    if (page === 'all') return set({ pages: ['all'] });
    const rest = draft.pages.filter((entry) => entry !== 'all');
    const next = rest.includes(page) ? rest.filter((entry) => entry !== page) : [...rest, page];
    set({ pages: next.length > 0 ? next : ['all'] });
  };

  async function submit() {
    const input: PopupInput & { name: string; kind: PopupKind } = {
      name: draft.name.trim() || draft.title[locale] || draft.title.ar || draft.title.en,
      kind: draft.kind,
      isActive: draft.isActive,
      content: {
        ar: { title: draft.title.ar, body: draft.body.ar, cta: draft.cta.ar },
        en: { title: draft.title.en, body: draft.body.en, cta: draft.cta.en },
      },
      imageKey: draft.image?.key ?? null,
      ctaHref: draft.kind === 'NEWSLETTER' ? null : draft.ctaHref.trim() || null,
      discountId: draft.discountId || null,
      trigger: draft.trigger,
      triggerValue: whole(draft.triggerValue, 8),
      pages: draft.pages,
      audience: draft.audience,
      devices: draft.devices,
      frequencyDays: whole(draft.frequencyDays, 7),
      priority: whole(draft.priority, 0),
      startsAt: fromLocalInput(draft.startsAt),
      endsAt: fromLocalInput(draft.endsAt),
    };
    if (await save(popup?.id ?? null, input)) onClose();
  }

  return (
    <Sheet
      open
      size="lg"
      title={popup ? t('popupEdit') : t('popupNew')}
      subtitle={popup?.name}
      onClose={onClose}
      footer={
        <>
          {saveError ? (
            <span className="me-auto">
              <Alert>{saveError}</Alert>
            </span>
          ) : null}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button disabled={saving || (!draft.title.ar.trim() && !draft.title.en.trim())} onClick={() => void submit()}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <Choices<PopupKind>
          legend={t('popupKind')}
          name="popup-kind"
          columns={3}
          value={draft.kind}
          onChange={(kind) => set({ kind })}
          options={POPUP_KINDS.map((kind) => ({ value: kind, label: t(`popupKind_${kind}`), hint: t(`popupKindHint_${kind}`) }))}
        />

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="flex flex-col gap-4">
            <TextField label={t('popupName')} hint={t('popupNameHint')} value={draft.name} onChange={(name) => set({ name })} />
            <BilingualField label={t('popupTitle')} value={draft.title} limit={POPUP_LIMITS.title} onChange={(title) => set({ title })} />
            <BilingualField label={t('popupBody')} multiline rows={3} value={draft.body} limit={POPUP_LIMITS.body} onChange={(body) => set({ body })} />
            <BilingualField
              label={t('popupCta')}
              value={draft.cta}
              limit={POPUP_LIMITS.cta}
              placeholders={draft.kind === 'NEWSLETTER' ? { ar: 'أرسل لي الكود', en: 'Get my code' } : { ar: 'تسوّق الآن', en: 'Shop now' }}
              onChange={(cta) => set({ cta })}
            />
            {draft.kind !== 'NEWSLETTER' ? (
              <TextField label={t('popupLink')} hint={t('popupLinkHint')} latin value={draft.ctaHref} placeholder="/categories/bags" onChange={(ctaHref) => set({ ctaHref })} />
            ) : null}
            <Field label={draft.kind === 'OFFER' ? t('popupCoupon') : t('popupCouponOptional')} htmlFor="popup-coupon" hint={t(`popupCouponHint_${draft.kind}`)}>
              <AsyncSelect
                id="popup-coupon"
                value={draft.discountId}
                emptyLabel={t('popupNoCoupon')}
                fetchPage={couponOptions.page}
                fetchOne={couponOptions.one}
                onChange={(discountId) => set({ discountId })}
              />
            </Field>
            <MediaField
              kind="image"
              label={t('popupImage')}
              hint={t('popupImageHint')}
              portrait={false}
              value={draft.image}
              onChange={(next) => set({ image: (next as { key: string; url: string } | null) ?? null })}
            />
          </div>
          <PopupPreview draft={draft} />
        </div>

        <AnnotatedSection title={t('popupWhenTitle')} description={t('popupWhenHint')}>
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label={t('popupTriggerLabel')}
                value={draft.trigger}
                options={POPUP_TRIGGERS.map((trigger) => ({ value: trigger, label: t(`popupTriggerName_${trigger}`) }))}
                onChange={(trigger) => set({ trigger: trigger as Draft['trigger'] })}
              />
              <TextField
                label={draft.trigger === 'SCROLL' ? t('popupTriggerPercent') : t('popupTriggerSeconds')}
                type="number"
                inputMode="numeric"
                latin
                value={draft.triggerValue}
                onChange={(triggerValue) => set({ triggerValue })}
              />
            </div>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium text-ink">{t('popupPages')}</legend>
              <div className="flex flex-wrap gap-2">
                {POPUP_PAGES.map((page) => (
                  <button
                    key={page}
                    type="button"
                    aria-pressed={draft.pages.includes(page)}
                    onClick={() => togglePage(page)}
                    className={cx(
                      'rounded-full border px-3 py-1.5 text-sm',
                      draft.pages.includes(page) ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line text-ink hover:border-brand-300',
                    )}
                  >
                    {t(`popupPage_${page}`)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-ink-muted">{t('popupPagesHint')}</p>
            </fieldset>
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label={t('popupAudience')}
                value={draft.audience}
                options={POPUP_AUDIENCES.map((audience) => ({ value: audience, label: t(`popupAudience_${audience}`) }))}
                onChange={(audience) => set({ audience: audience as Draft['audience'] })}
              />
              <SelectField
                label={t('popupDevices')}
                value={draft.devices}
                options={POPUP_DEVICES.map((devices) => ({ value: devices, label: t(`popupDevices_${devices}`) }))}
                onChange={(devices) => set({ devices: devices as Draft['devices'] })}
              />
              <TextField label={t('popupFrequency')} hint={t('popupFrequencyHint')} type="number" inputMode="numeric" latin value={draft.frequencyDays} onChange={(frequencyDays) => set({ frequencyDays })} />
              <TextField label={t('popupPriority')} hint={t('popupPriorityHint')} type="number" inputMode="numeric" latin value={draft.priority} onChange={(priority) => set({ priority })} />
              <Field label={t('popupStarts')} htmlFor="popup-starts">
                <Input id="popup-starts" type="datetime-local" dir="ltr" value={draft.startsAt} onChange={(event) => set({ startsAt: event.target.value })} />
              </Field>
              <Field label={t('popupEnds')} htmlFor="popup-ends">
                <Input id="popup-ends" type="datetime-local" dir="ltr" value={draft.endsAt} onChange={(event) => set({ endsAt: event.target.value })} />
              </Field>
            </div>
            <ToggleField label={t('popupOn')} hint={t('popupOnHint')} checked={draft.isActive} onChange={(isActive) => set({ isActive })} />
          </div>
        </AnnotatedSection>
      </div>
    </Sheet>
  );
}

/** How the popup will read, in the language being read here. */
function PopupPreview({ draft }: { draft: Draft }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const pick = (pair: BilingualValue) => pair[locale] || pair.ar || pair.en;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-ink-muted">{t('popupPreview')}</span>
      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-card">
        {draft.image ? <img src={draft.image.url} alt="" className="aspect-[16/9] w-full object-cover" /> : null}
        <div className="flex flex-col gap-2 p-4 text-center">
          <strong className="text-base text-ink">{pick(draft.title) || t('popupTitle')}</strong>
          {pick(draft.body) ? <p className="whitespace-pre-line text-xs text-ink-muted">{pick(draft.body)}</p> : null}
          {draft.kind === 'NEWSLETTER' ? <div className="h-8 rounded-control border border-line text-start text-xs leading-8 text-ink-faint ps-2">name@example.com</div> : null}
          {draft.kind === 'OFFER' && draft.discountId ? (
            <div className="mx-auto rounded-control border-2 border-dashed border-brand-600 px-3 py-1 font-mono text-sm font-bold">CODE</div>
          ) : null}
          <div className="rounded-control bg-brand-600 py-2 text-xs font-semibold text-brand-ink">{pick(draft.cta) || '…'}</div>
        </div>
      </div>
    </div>
  );
}

// ─── The announcement bar ────────────────────────────────────────────────────

const TONE_SWATCH: Record<AnnouncementSetting['tone'], string> = {
  brand: 'bg-brand-600 text-brand-ink',
  dark: 'bg-ink text-surface',
  sale: 'bg-danger text-danger-ink',
  light: 'bg-brand-50 text-brand-700',
};

function barOf(value: unknown): AnnouncementSetting {
  const stored = (value ?? {}) as Partial<AnnouncementSetting>;
  return { ...ANNOUNCEMENT_DEFAULTS, ...stored, messages: stored.messages ?? [] };
}

function AnnouncementForm() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { entries, loading, saving, error, save } = useSettings();
  const stored = useMemo(() => barOf(settingValue(entries, BAR_KEY)), [entries]);
  const [draft, setDraft] = useState<AnnouncementSetting>(stored);
  useEffect(() => setDraft(stored), [stored]);

  if (loading && entries.length === 0) return <Skeleton className="h-96" />;
  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);
  const set = (patch: Partial<AnnouncementSetting>) => setDraft((current) => ({ ...current, ...patch }));
  const setMessage = (index: number, patch: Partial<AnnouncementSetting['messages'][number]>) =>
    set({ messages: draft.messages.map((message, at) => (at === index ? { ...message, ...patch } : message)) });
  const preview = draft.messages.map((message) => message[locale] || message.ar || message.en).filter(Boolean);

  return (
    <Card className="flex flex-col gap-8 p-5">
      <div className={cx('flex min-h-9 items-center justify-center rounded-control px-4 py-2 text-center text-[13px] font-medium', TONE_SWATCH[draft.tone])}>
        {preview[0] ?? t('announcementPreviewEmpty')}
      </div>

      <AnnotatedSection title={t('announcementMessages')} description={t('announcementMessagesHint')}>
        <div className="flex flex-col gap-4">
          {draft.messages.map((message, index) => (
            <fieldset key={index} className="flex flex-col gap-3 rounded-card border border-line p-4">
              <legend className="px-1 text-sm font-semibold text-ink">{t('announcementMessage', { n: String(index + 1) })}</legend>
              <BilingualField label={t('announcementText')} value={{ ar: message.ar, en: message.en }} limit={ANNOUNCEMENT_LIMITS.text} onChange={(text) => setMessage(index, text)} />
              <TextField label={t('popupLink')} hint={t('popupLinkHint')} latin value={message.href ?? ''} placeholder="/categories/bags" onChange={(href) => setMessage(index, { href })} />
              <Button variant="ghost" size="sm" className="self-end" onClick={() => set({ messages: draft.messages.filter((_, at) => at !== index) })}>
                {t('delete')}
              </Button>
            </fieldset>
          ))}
          {draft.messages.length < ANNOUNCEMENT_LIMITS.messages ? (
            <Button variant="secondary" className="self-start" onClick={() => set({ messages: [...draft.messages, { ar: '', en: '', href: null }] })}>
              <PlusIcon className="size-4" />
              {t('announcementAdd')}
            </Button>
          ) : null}
        </div>
      </AnnotatedSection>

      <AnnotatedSection title={t('announcementLook')} description={t('announcementLookHint')}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {ANNOUNCEMENT_TONES.map((tone) => (
              <button
                key={tone}
                type="button"
                aria-pressed={draft.tone === tone}
                onClick={() => set({ tone })}
                className={cx('rounded-full px-4 py-1.5 text-sm font-medium ring-offset-2', TONE_SWATCH[tone], draft.tone === tone && 'ring-2 ring-brand-500')}
              >
                {t(`announcementTone_${tone}`)}
              </button>
            ))}
          </div>
          <ToggleField label={t('announcementDismissible')} hint={t('announcementDismissibleHint')} checked={draft.dismissible} onChange={(dismissible) => set({ dismissible })} />
          <Field label={t('announcementCountdown')} htmlFor="bar-countdown" hint={t('announcementCountdownHint')}>
            <Input id="bar-countdown" type="datetime-local" dir="ltr" value={toLocalInput(draft.countdownTo)} onChange={(event) => set({ countdownTo: fromLocalInput(event.target.value) })} />
          </Field>
        </div>
      </AnnotatedSection>

      <AnnotatedSection title={t('announcementSchedule')} description={t('announcementScheduleHint')}>
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('popupStarts')} htmlFor="bar-starts">
              <Input id="bar-starts" type="datetime-local" dir="ltr" value={toLocalInput(draft.startsAt)} onChange={(event) => set({ startsAt: fromLocalInput(event.target.value) })} />
            </Field>
            <Field label={t('popupEnds')} htmlFor="bar-ends">
              <Input id="bar-ends" type="datetime-local" dir="ltr" value={toLocalInput(draft.endsAt)} onChange={(event) => set({ endsAt: fromLocalInput(event.target.value) })} />
            </Field>
          </div>
          <ToggleField label={t('announcementOn')} hint={t('announcementOnHint')} checked={draft.enabled} onChange={(enabled) => set({ enabled })} />
        </div>
      </AnnotatedSection>

      {error ? <Alert>{error}</Alert> : null}
      <SaveBar
        saving={saving === BAR_KEY}
        dirty={dirty}
        onSave={() =>
          void save(BAR_KEY, { ...draft, messages: draft.messages.map((message) => ({ ar: message.ar.trim(), en: message.en.trim(), href: message.href?.trim() || null })) })
        }
        onDiscard={() => setDraft(stored)}
      />
    </Card>
  );
}
